import { anchor } from './setting-anchors.types';

import type { SettingAnchor } from './setting-anchors.types';

/**
 * The `/docs/settings` and `/settings/billing` anchors.
 *
 * Two pages that genuinely hold several unrelated decisions each, which is what
 * makes them worth indexing at card level rather than by page name: "how do we
 * write" and "how do we take the docs back down" are not the same question, and
 * neither is "what plan are we on" and "what are we about to be charged".
 *
 * Their own file for the reason the workspace and limits blocks have one — the
 * registry crossed the ~300-line ceiling, and a whole page's anchors is a real
 * unit rather than an arbitrary slice.
 */
export const DOCS_BILLING_SETTING_ANCHORS = {
  // `/docs/settings` — the three cards, in the order they render.
  //
  // `documentation:view` on all three rather than the action each WRITE needs:
  // the card renders for a reader (with a `PermissionHint` where it does), so
  // it is a legitimate landing, and claiming `admin` would hide the editorial
  // policy from the person who has to read it before writing anything.
  'docs-editorial-policy': anchor(
    'setting-docs-editorial-policy',
    '/docs/settings',
    'docsHub.settings.policy.title',
    'documentation',
    'view',
    { keywordsKey: 'settingSearch.docsEditorialPolicy' },
  ),
  'docs-export': anchor(
    'setting-docs-export',
    '/docs/settings',
    'docsExport.title',
    'documentation',
    'view',
    { keywordsKey: 'settingSearch.docsExport' },
  ),
  'docs-recall': anchor(
    'setting-docs-recall',
    '/docs/settings',
    'docsRecall.zone.title',
    'documentation',
    'view',
    { keywordsKey: 'settingSearch.docsRecall' },
  ),

  // `/settings/billing` — `billing:view` throughout. The page is owner-and-admin
  // territory by grant rather than by an extra condition, so the surface matrix
  // says everything there is to say.
  'billing-plan': anchor(
    'setting-billing-plan',
    '/settings/billing',
    'billing.currentPlan.label',
    'billing',
    'view',
    { keywordsKey: 'settingSearch.billingPlan' },
  ),
  'billing-projected-invoice': anchor(
    'setting-billing-projected-invoice',
    '/settings/billing',
    'billing.projected.heading',
    'billing',
    'view',
    { keywordsKey: 'settingSearch.billingProjectedInvoice' },
  ),
  'billing-spend': anchor(
    'setting-billing-spend',
    '/settings/billing',
    'billing.spend.heading',
    'billing',
    'view',
    { keywordsKey: 'settingSearch.billingSpend' },
  ),
} as const satisfies Record<string, SettingAnchor>;
